import { ANSWER_POSTS, type AnswerPost } from "@/lib/answers-data";
import { TRADE_HUBS, type TradeHub, findTradeHub } from "@/lib/site-data";

/**
 * Map every /seo/* trade slug (matrix + programmatic) to the canonical
 * hub slug + the answers-collection tradeSlug they share content with.
 * This is what powers the automatic internal-link network from
 * /seo/{trade}/{city}[/{problem}] → /hubs/{trade} and → /answers/{slug}.
 */
const SEO_TRADE_TO_HUB: Record<string, TradeHub["slug"]> = {
  plumbing: "plumber",
  plumber: "plumber",
  "drain-cleaning": "plumber",
  "leak-detection": "plumber",
  sewer: "plumber",
  "water-heater": "plumber",
  electrical: "electrician",
  electrician: "electrician",
  "generator-installation": "electrician",
  hvac: "hvac",
  "furnace-repair": "hvac",
  roofing: "roofer",
  roofer: "roofer",
};

const HUB_TO_ANSWERS_TRADE: Record<TradeHub["slug"], string> = {
  plumber: "plumber",
  electrician: "electrician",
  hvac: "hvac",
  roofer: "roofer",
};

export function hubForSeoTrade(seoTrade: string): TradeHub | undefined {
  const hubSlug = SEO_TRADE_TO_HUB[seoTrade];
  if (!hubSlug) return undefined;
  return findTradeHub(hubSlug);
}

/** Deterministic, token-overlap scored answer picks for a given seo trade + focus text. */
export function relatedAnswersFor(
  seoTrade: string,
  focusText: string,
  limit = 4,
): AnswerPost[] {
  const hubSlug = SEO_TRADE_TO_HUB[seoTrade];
  if (!hubSlug) return [];
  const answersTrade = HUB_TO_ANSWERS_TRADE[hubSlug];
  const pool = ANSWER_POSTS.filter((p) => p.tradeSlug === answersTrade);
  if (pool.length === 0) return [];

  const tokens = new Set(
    (focusText + " " + seoTrade)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 3),
  );

  const scored = pool.map((p) => {
    const hay = `${p.title} ${p.keyword} ${p.qSlug}`.toLowerCase();
    let score = 0;
    for (const t of tokens) if (hay.includes(t)) score += 1;
    return { p, score };
  });

  // Stable order: score desc, then slug asc — deterministic per page.
  scored.sort((a, b) => b.score - a.score || a.p.slug.localeCompare(b.p.slug));

  const picks: AnswerPost[] = [];
  for (const { p } of scored) {
    picks.push(p);
    if (picks.length >= limit) break;
  }
  if (picks.length < limit) {
    for (const p of pool) {
      if (picks.length >= limit) break;
      if (!picks.includes(p)) picks.push(p);
    }
  }
  return picks;
}

export function otherHubs(currentSlug: TradeHub["slug"] | undefined): TradeHub[] {
  return TRADE_HUBS.filter((h) => h.slug !== currentSlug);
}